import type { GuideLang } from "@/lib/virtualspace/guide/guide-public-shape";

/** Guide dates are Cairo calendar days, in the reader's language. */
const ZONE = "Africa/Cairo";
const locale = (lang: GuideLang) => (lang === "ar" ? "ar-EG" : "en-GB");

/** The month and day for an event's date badge: { mon: "OCT", day: "15" }. */
export function eventBadge(startsAt: string, lang: GuideLang): { mon: string; day: string } {
  const date = new Date(startsAt);
  const mon = new Intl.DateTimeFormat(locale(lang), { timeZone: ZONE, month: lang === "ar" ? "long" : "short" }).format(date);
  const day = new Intl.DateTimeFormat(locale(lang), { timeZone: ZONE, day: "2-digit" }).format(date);
  return { mon: lang === "ar" ? mon : mon.toLocaleUpperCase("en"), day };
}

const cairoYear = (date: Date) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: ZONE, year: "numeric" }).format(date);

/**
 * "15–23 October", "11 October – 1 November", or one day when it starts and
 * ends the same day. The year is added when the event isn't in this year.
 */
export function eventDates(startsAt: string, endsAt: string | null, lang: GuideLang, now: Date = new Date()): string {
  const start = new Date(startsAt);
  const otherYear = [start, ...(endsAt ? [new Date(endsAt)] : [])].some((d) => cairoYear(d) !== cairoYear(now));
  const fmt = new Intl.DateTimeFormat(locale(lang), {
    timeZone: ZONE,
    day: "numeric",
    month: "long",
    ...(otherYear ? { year: "numeric" as const } : {}),
  });
  if (!endsAt) return fmt.format(start);
  const end = new Date(endsAt);
  return fmt.formatRange(start, end);
}

/** News is dated by month: "September 2026". */
export function newsMonth(publishedAt: string, lang: GuideLang): string {
  return new Intl.DateTimeFormat(locale(lang), { timeZone: ZONE, month: "long", year: "numeric" }).format(
    new Date(publishedAt),
  );
}

/** "2026-09": the Cairo month an item falls in, for grouping lists by month. */
export function cairoMonthKey(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: ZONE, year: "numeric", month: "2-digit" }).formatToParts(
    new Date(iso),
  );
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}`;
}

export type EventWindow = "all" | "now" | "week" | "month";

/**
 * Whether an event falls in a quick-pick window: on now (running), this
 * week (running or starting within seven days), this month (running or
 * starting before the end of the Cairo calendar month).
 */
export function eventInWindow(event: { startsAt: string; endsAt: string | null }, window: EventWindow, now: Date): boolean {
  if (window === "all") return true;
  const start = new Date(event.startsAt).getTime();
  const end = new Date(event.endsAt ?? event.startsAt).getTime();
  const running = start <= now.getTime() && now.getTime() <= end;
  if (window === "now") return running;
  if (running) return true;
  if (window === "week") return start > now.getTime() && start <= now.getTime() + 7 * 86_400_000;
  return start > now.getTime() && cairoMonthKey(event.startsAt) === cairoMonthKey(now.toISOString());
}

/** Items grouped by Cairo month, keeping their order. */
export function groupByMonth<T>(items: T[], dateOf: (item: T) => string): Array<{ key: string; first: string; items: T[] }> {
  const groups: Array<{ key: string; first: string; items: T[] }> = [];
  for (const item of items) {
    const key = cairoMonthKey(dateOf(item));
    const last = groups[groups.length - 1];
    if (last?.key === key) last.items.push(item);
    else groups.push({ key, first: dateOf(item), items: [item] });
  }
  return groups;
}
